import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/03-presenters/components/ui/tabs";
import Image from "next/image";
import { Input } from "@/03-presenters/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import HighlightedText from "@/03-presenters/components/HighlightedText";
import { Select, SelectItem, Skeleton } from "@nextui-org/react";
import { intervalToDuration } from "date-fns";
import { formatNumber, countryList } from "@/00-shared/const";
import { ReactRenderer } from "@tiptap/react";
import tippy, { Instance } from "tippy.js";
import { PluginKey } from "@tiptap/pm/state";
import { SuggestionOptions } from "@tiptap/suggestion";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, PencilLine } from "lucide-react";
import { useDebounceFn } from "ahooks";
import { parseInputContent } from "./utils";
import { useGlobalStore, useStore } from "./StoreProvider";
interface EntityItem {
  entity_id: string;
  field_list: {
    field_key: string;
    field_type: string;
    value: any;
  }[];
}
export interface FormattedEntityItem {
  id: string;
  avatar: string;
  name: string;
  rawData: Record<string, any>;
  type: string;
  extraInfo?: string;
  region: string;
}
interface MentionPopupProps {
  onSelect: (item: FormattedEntityItem) => void;
  onClose: () => void;
}
interface MentionPopupRef {}
const tabItems = {
  creator: {
    url: "creator/search/compact",
    label: "达人"
  },
  shop: {
    url: "seller/search/compact",
    label: "店铺"
  },
  product: {
    url: "product/search/compact",
    label: "商品"
  },
  video: {
    url: "video/search/compact",
    label: "视频"
  },
  live: {
    url: "live/search/compact",
    label: "直播"
  },
  ad: {
    url: "ads/search/compact",
    label: "广告"
  }
};

/**
 * @description {@} 的 popup
 */
const MentionPopup = forwardRef<MentionPopupRef, MentionPopupProps>(({
  onClose,
  onSelect
}, ref) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeItem, setActiveItem] = useState<string>("creator");
  const [keyword, setKeyword] = useState<string>();
  const mentionPopupRef = useRef<HTMLDivElement>(null);
  const isAiChatPage = window.location.pathname.includes("/ai");
  const currentEntity = useGlobalStore(state => state.currentEntity);
  const setCurrentEntity = useGlobalStore(state => state.setCurrentEntity);
  const [selectedCountry, setSelectedCountry] = useState<string[]>(["US"]);
  useImperativeHandle(ref, () => ({}));
  const formatFetchData = (data: EntityItem[]): FormattedEntityItem[] => {
    return data.map(item => {
      const fieldData = Object.fromEntries(item.field_list.map(field => [field.field_key, field.value]));
      const fieldMappings: Record<string, Record<string, string>> = {
        avatar: {
          creator: "tiktok_creator_search_user_avatar",
          shop: "tiktok_seller_search_seller_avatar",
          product: "tiktok_product_search_product_images",
          video: "tiktok_video_search_video_cover",
          live: "tiktok_live_search_cover",
          ad: "tiktok_ads_search_ad_cover"
        },
        name: {
          creator: "tiktok_creator_search_nickname",
          shop: "tiktok_seller_search_shop_name",
          product: "tiktok_product_search_product_name",
          video: "tiktok_video_search_video_desc",
          live: "tiktok_live_search_title",
          ad: "tiktok_ads_search_ad_title"
        }
      };
      const getFieldValue = (fieldType: string): any => {
        const fieldKeys = fieldMappings[fieldType][activeItem as keyof typeof tabItems];
        const value = fieldData[fieldKeys];
        if (value) {
          if (fieldType === "avatar") {
            if (Array.isArray(value)) {
              return value[0].url;
            }
            return value.url;
          }
          return value;
        }
        return "";
      };

      // 获取额外信息
      const getExtraInfo = (): string => {
        if (activeItem === "creator") {
          const followerCount = formatNumber(fieldData.tiktok_creator_search_follower_count);
          return followerCount ? `粉丝数: ${followerCount}` : "";
        } else if (activeItem === "shop") {
          const rating = fieldData.tiktok_seller_search_shop_rating;
          return rating ? `评分: ${rating}` : "";
        } else if (activeItem === "product") {
          const price = fieldData.tiktok_product_search_price;
          return price ? `价格: ${price}` : "";
        } else if (activeItem === "video") {
          const views = formatNumber(fieldData.tiktok_video_search_video_like_count);
          return views ? `点赞数: ${views}` : "";
        } else if (activeItem === "live") {
          const startTime = fieldData.tiktok_live_search_start_time;
          const endTime = fieldData.tiktok_live_search_end_time;
          const milliseconds = startTime && endTime ? endTime - startTime : 0;
          const duration = intervalToDuration({
            start: 0,
            end: milliseconds
          });
          if (milliseconds) {
            const hours = duration.hours || 0;
            const minutes = duration.minutes || 0;
            const seconds = duration.seconds || 0;
            let result = "";
            if (hours > 0) result += `${hours}小时`;
            if (minutes > 0) result += `${minutes}分钟`;
            if (seconds > 0) result += `${seconds}秒`;
            return `直播时间: ${result}`;
          }
        } else if (activeItem === "ad") {
          const playCount = formatNumber(fieldData.tiktok_ads_search_video_play_count);
          return playCount ? `播放量: ${playCount}` : "";
        }
        return "";
      };
      return {
        id: item.entity_id,
        avatar: getFieldValue("avatar"),
        name: getFieldValue("name"),
        rawData: fieldData,
        // 保留原始数据，以便需要时使用
        type: activeItem,
        // 添加类型信息
        extraInfo: getExtraInfo(),
        region: selectedCountry[0]
      };
    });
  };
  const {
    data,
    isLoading
  } = useQuery({
    queryKey: [activeItem, keyword, selectedCountry],
    queryFn: async () => {
      const url = tabItems[activeItem as keyof typeof tabItems].url;
      const res = await fetch(`/api/v3/entity/tiktok/${url}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-biyi-data-region": selectedCountry[0]
        },
        body: JSON.stringify({
          limit: 10,
          offset: 0,
          keyword_filter: keyword
        })
      }).then(res => res.json());
      const data = res.entity_list as EntityItem[];
      return {
        list: formatFetchData(data),
        highlight: res.highlight || []
      };
    }
  });
  const {
    run: setKeywordDebounce
  } = useDebounceFn((value: string) => {
    setKeyword(value);
  }, {
    wait: 500
  });
  useEffect(() => {
    const mentionPopup = mentionPopupRef.current;
    if (!mentionPopup) return;
    const handleKeyEvents = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    mentionPopup.addEventListener("keyup", handleKeyEvents);
    return () => {
      mentionPopup.removeEventListener("keyup", handleKeyEvents);
    };
  }, [mentionPopupRef]);
  useEffect(() => {
    if (currentEntity) {
      setActiveItem(currentEntity);
      setCurrentEntity("");
    }
  }, [currentEntity]);
  return <div ref={mentionPopupRef} className={cn("w-[400px] rounded-lg border p-3 shadow-xl", isAiChatPage ? "border-gray-300 bg-white" : "border-[#646E97] bg-[#1b1a28] shadow shadow-[#646E97]")} onClick={e => e.stopPropagation()}>
        <div className="relative">
          <Input ref={inputRef} placeholder="请搜索想要分析的对象" className={cn("mb-2 h-10", !isAiChatPage && "border-[#646E97] bg-[#1B1A28] text-white")} onChange={e => {
        setKeywordDebounce(e.target.value);
      }} />
          <div className="absolute right-1 top-1 z-[10000]">
            <Select aria-label="Country" classNames={{
          trigger: cn("min-h-8 h-8 rounded-sm [&>svg]:right-1 px-1", !isAiChatPage && "bg-[#37374C] text-[#94A5BD] data-[hover=true]:bg-[#37374C]"),
          listboxWrapper: "!scrollbar-none",
          innerWrapper: "w-full mr-4"
        }} popoverProps={{
          classNames: {
            base: "w-[140px]",
            content: !isAiChatPage && "bg-[#37374C] text-[#94A5BD]"
          }
        }} selectedKeys={selectedCountry} onSelectionChange={keys => {
          setSelectedCountry(Array.from(keys) as string[]);
        }} disallowEmptySelection={true} renderValue={keys => {
          const country = keys[0] as {
            key: string;
            textValue: string;
          };
          const countryImageUrl = `/images/flag/${country.key.toLowerCase()}.png`;
          return <div className="flex items-center gap-1">
                    <div className="relative h-4 w-4">
                      <Image className="object-contain" src={countryImageUrl} fill alt="flag" />
                    </div>
                    <span className={cn(!isAiChatPage && "text-[#94A5BD]")}>
                      {country.textValue}
                    </span>
                  </div>;
        }}>
              {countryList.map(item => {
            const countryImageUrl = `/images/flag/${item.code.toLowerCase()}.png`;
            return <SelectItem key={item.code} value={item.code} textValue={item.name} className={cn(!isAiChatPage && "data-[focus=true]:!bg-[#525270] data-[hover=true]:!bg-[#525270] data-[focus=true]:!text-[#B9BBFF] data-[hover=true]:!text-[#B9BBFF] hover:!bg-[#525270] hover:!text-[#B9BBFF]")}>
                    <div className="flex items-center gap-1">
                      <div className="relative h-4 w-4">
                        <Image className="object-contain" src={countryImageUrl} fill alt="flag" />
                      </div>
                      <span>{item.name}</span>
                    </div>
                  </SelectItem>;
          })}
            </Select>
          </div>
        </div>
        <div>
          <Tabs value={activeItem} onValueChange={setActiveItem}>
            <TabsList>
              {Object.entries(tabItems).map(([key, item]) => <TabsTrigger key={key} value={key} className={cn(!isAiChatPage && "!bg-[#1b1a28] !text-[#646E97] data-[state=active]:!text-[#B9BBFF]")} id={`${key}-tab`}>
                  {item.label}
                </TabsTrigger>)}
            </TabsList>

            <div className={cn("mt-2 flex h-[200px] flex-col gap-2 overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-200", !isAiChatPage && "scrollbar-thumb-[#64647f]")}>
              {isLoading ? <div className="flex flex-col gap-6">
                  {Array.from({
              length: 3
            }).map((_, index) => <div key={index} className="flex w-full max-w-[300px] items-center gap-2 px-2 py-1">
                      <div>
                        <Skeleton className="flex h-10 w-10 rounded-full" />
                      </div>
                      <div className="flex w-full flex-col gap-2">
                        <Skeleton className="h-3 w-4/5 rounded-lg" />
                        <Skeleton className="h-3 w-2/5 rounded-lg" />
                      </div>
                    </div>)}
                </div> : data?.list.length ? <>
                  {data.list.map(item => <TabsContent key={item.id} value={activeItem}>
                      <div className={cn("flex cursor-pointer gap-2 rounded-sm px-2 py-1.5 hover:bg-[#f1f1ff]", !isAiChatPage && "hover:!bg-[#37374c]")} onClick={() => onSelect(item)}>
                        <div>
                          <Image src={item.avatar} alt="avatar" width={0} height={0} className={cn("h-10 w-10 object-cover", ["creator"].includes(item.type) && "rounded-full")} />
                        </div>
                        <div className="flex flex-1 flex-col justify-around overflow-hidden">
                          <div className={cn("text-sm", item.type === "product" ? "line-clamp-2" : "truncate", !isAiChatPage && "!text-white")}>
                            <HighlightedText text={item.name} keyword={keyword ?? ""} highlighList={data.highlight} />
                          </div>
                          {item.extraInfo && <div className="flex items-center gap-2 text-xs text-gray-500">
                              {item.extraInfo}
                            </div>}
                        </div>
                      </div>
                    </TabsContent>)}
                </> : <div className="flex h-full items-center justify-center text-gray-500">
                  暂无数据
                </div>}
            </div>
          </Tabs>
        </div>
      </div>;
});
MentionPopup.displayName = "MentionPopup";
interface SlashCommandPopupProps {
  onClose: () => void;
  onSelect: (item: string) => void;
  home?: boolean;
}
export const ActiveTemplate = ({
  content,
  home = false,
  className
}: {
  content: (string | string[])[];
  home?: boolean;
  className?: string;
}) => content.map(item => {
  if (Array.isArray(item)) {
    return <span key={item[0]} className={cn("flex items-center font-medium text-[#4A50FF]", home && "text-[#7A76BB]", className)}>
          <span>@</span>
          <span>{item[0]}</span>
          <PencilLine className="h-3 w-3" />
        </span>;
  }
  return <span key={item}>{item}</span>;
});

/** @description {/} 的 popup */
const SlashCommandPopup = forwardRef<MentionPopupRef, SlashCommandPopupProps>(({
  onClose,
  onSelect
}, ref) => {
  const mentionPopupRef = useRef<HTMLDivElement>(null);
  const [activeItem, setActiveItem] = useState<number>(0);
  const [activeTemplate, setActiveTemplate] = useState<number>(0);
  const isAiChatPage = window.location.pathname.includes("/ai");
  const setCurrentEntity = useGlobalStore(state => state.setCurrentEntity);
  const store = useStore();
  const slashCommandTemplateList = store(state => state.slashCommandTemplateList);
  const onValueChange = (value: string) => {
    setActiveItem(Number(value));
    setActiveTemplate(0);
  };
  const handleSelect = (item: (typeof slashCommandTemplateList)[number]["template"][number]) => {
    setCurrentEntity(item.entity ?? "");
    onSelect(item.inputContent);
  };
  const handleKeyEvents = (e: KeyboardEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (e.key === "Escape") {
      onClose();
      setCurrentEntity("");
    }
    if (e.key === "ArrowUp" && activeTemplate > 0) {
      setActiveTemplate(activeTemplate - 1);
    }
    if (e.key === "ArrowDown" && activeTemplate < slashCommandTemplateList[activeItem].template.length - 1) {
      setActiveTemplate(activeTemplate + 1);
    }
    if (e.key === "Enter") {
      handleSelect(slashCommandTemplateList[activeItem].template[activeTemplate]);
    }
  };
  useEffect(() => {
    const mentionPopup = mentionPopupRef.current;
    if (!mentionPopup) return;
    mentionPopup.addEventListener("keyup", handleKeyEvents);
    return () => {
      mentionPopup.removeEventListener("keyup", handleKeyEvents);
    };
  }, [mentionPopupRef, activeTemplate, activeItem]);
  return <div ref={mentionPopupRef} className={cn("rounded-lg border p-3 shadow-xl", isAiChatPage ? "border-gray-300 bg-white" : "border-[#646E97] bg-[#1b1a28] shadow shadow-[#646E97]")} onClick={e => e.stopPropagation()}>
        <div className="mb-2 flex items-center justify-between  text-xs text-gray-400">
          <span>猜你想问</span>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1">
              <ArrowLeft className="h-3 w-3" />
              <ArrowRight className="h-3 w-3" />
              <span>切分类</span>
            </span>
            <span>|</span>
            <span className="flex items-center gap-1">
              <ArrowUp className="h-3 w-3" />
              <ArrowDown className="h-3 w-3" />
              <span>切问题</span>
            </span>
          </div>
        </div>

        <Tabs value={activeItem.toString()} onValueChange={onValueChange}>
          <TabsList>
            {slashCommandTemplateList.map((item, index) => <TabsTrigger key={item.type} value={index.toString()} className={cn("border-b-2 border-b-transparent data-[state=active]:border-b-[#4A50FF] focus-visible:ring-transparent focus-visible:ring-offset-0", !isAiChatPage && "!bg-[#1b1a28] !text-[#646E97] data-[state=active]:!text-[#B9BBFF]")} id={`${item.type}-tab`}>
                {item.name}
              </TabsTrigger>)}
          </TabsList>

          <div className="mt-2 flex flex-col gap-2">
            {slashCommandTemplateList.map((item, index) => <TabsContent key={item.type} value={index.toString()}>
                {item.template.map((child, childIndex) => <div key={child.title} className={cn("flex cursor-pointer rounded-sm px-2 py-3 text-sm", !isAiChatPage && "text-[#999999]", activeTemplate === childIndex && (!isAiChatPage ? "bg-[#37374c] text-white" : "bg-[#f1f1ff]"))} onClick={() => handleSelect(child)} onMouseEnter={() => setActiveTemplate(childIndex)}>
                    {activeTemplate === childIndex ? <ActiveTemplate content={child.content} home={!isAiChatPage} /> : child.title}
                  </div>)}
              </TabsContent>)}
          </div>
        </Tabs>
      </div>;
});
SlashCommandPopup.displayName = "SlashCommandPopup";

/** @abstract {@} 提及扩展 */
const MentionSuggestion: Omit<SuggestionOptions, "editor"> = {
  char: "@",
  pluginKey: new PluginKey("mention-at"),
  render: () => {
    let component: ReactRenderer;
    let popup: Instance<any>[];
    return {
      onStart: props => {
        component = new ReactRenderer(MentionPopup, {
          props: {
            ...props,
            onSelect: (item: FormattedEntityItem) => {
              props.command({
                id: item.id,
                type: item.type,
                label: item.name,
                region: item.region
              });
            },
            onClose: () => {
              popup[0].hide();
              props.editor.commands.focus();
            }
          },
          editor: props.editor
        }) as ReactRenderer;
        if (!props.clientRect) {
          return;
        }
        const instances = tippy(document.body, {
          getReferenceClientRect: () => props.clientRect?.() || new DOMRect(),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "top-start",
          onShow(instance) {
            setTimeout(() => {
              const input = instance.popper.querySelector("input");
              if (input) input.focus();
            }, 0);
          }
        });
        popup = Array.isArray(instances) ? instances : [instances];
      },
      onExit() {
        component.destroy();
        popup[0].destroy();
      }
    };
  }
};

/**  @abstract {/} 命令扩展 */
const SlashSuggestion: Omit<SuggestionOptions, "editor"> = {
  char: "/",
  pluginKey: new PluginKey("mention-slash"),
  render: () => {
    let component: ReactRenderer;
    let popup: Instance<any>[];
    return {
      onStart: props => {
        document.body.style.overflow = "hidden";
        component = new ReactRenderer(SlashCommandPopup, {
          props: {
            ...props,
            onSelect: (item: string) => {
              popup[0].hide();
              parseInputContent(props.editor, item);
            },
            onClose: () => {
              document.body.style.overflow = "";
              popup[0].hide();
              props.editor.commands.focus();
            }
          },
          editor: props.editor
        }) as ReactRenderer;
        if (!props.clientRect) {
          return;
        }
        const instances = tippy(document.body, {
          getReferenceClientRect: () => props.clientRect?.() || new DOMRect(),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "top-start",
          maxWidth: "100%",
          onShow(instance) {
            setTimeout(() => {
              const button = instance.popper.querySelector("button");
              if (button) button.focus();
            }, 0);
          }
        });
        popup = Array.isArray(instances) ? instances : [instances];
      },
      onExit() {
        component.destroy();
        popup[0].destroy();
      }
    };
  }
};
export { MentionSuggestion, SlashSuggestion };